import { Box, Button, ButtonBase, TextField, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

import { brand } from '../../styles/tokens';

/**
 * Auth shell (guide 7.13, split variant): a Canopy brand panel that carries
 * the AvaliaHub lockup, a tagline and a low-contrast hub watermark, next to
 * a 400px card on the canvas with label-above fields, a full-width 44px
 * primary button and ghost secondary actions. Shared by Login, ForgotPassword
 * and ResetPassword so the whole auth flow reads as one place.
 */
export const Screen = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 3fr)',
  minHeight: '100vh',
  backgroundColor: theme.palette.background.default,

  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: 'minmax(0, 1fr)',
    gridTemplateRows: 'auto 1fr',
  },
}));

export const BrandPanel = styled(Box)(({ theme }) => {
  const { space } = theme.palette.tokens;
  return {
    position: 'relative',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    gap: space[8],
    padding: space[10],
    backgroundColor: brand.deepGreen,
    color: brand.white,

    [theme.breakpoints.down('md')]: {
      flexDirection: 'row',
      alignItems: 'center',
      minHeight: '72px',
      padding: `${space[4]}px ${space[6]}px`,
    },
  };
});

export const BrandTop = styled(Box)(() => ({
  position: 'relative',
  zIndex: 1,
  display: 'flex',
  alignItems: 'center',
}));

export const BrandBottom = styled(Box)(({ theme }) => ({
  position: 'relative',
  zIndex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.palette.tokens.space[3],
  maxWidth: '360px',

  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
}));

export const BrandTagline = styled(Typography)(({ theme }) => ({
  ...theme.typography.h5,
  color: brand.white,
  opacity: 0.92,
}));

export const BrandFooter = styled(Typography)(({ theme }) => ({
  ...theme.typography.caption,
  color: brand.white,
  opacity: 0.56,
}));

/** The hub symbol, oversized and faded, anchored to the panel's corner. */
export const BrandWatermark = styled(Box)(({ theme }) => ({
  position: 'absolute',
  right: '-120px',
  bottom: '-140px',
  width: '560px',
  height: '560px',
  opacity: 0.1,
  pointerEvents: 'none',
  userSelect: 'none',

  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
}));

export const FormPanel = styled('main')(({ theme }) => ({
  minWidth: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.palette.tokens.space[8],

  [theme.breakpoints.down('md')]: {
    alignItems: 'flex-start',
    padding: theme.palette.tokens.space[5],
  },
}));

export const AuthCard = styled('form')(({ theme }) => {
  const { space, radius } = theme.palette.tokens;
  return {
    width: '100%',
    minWidth: 0,
    maxWidth: '400px',
    display: 'flex',
    flexDirection: 'column',
    gap: space[5],
    padding: space[10],
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: `${radius.modal}px`,

    [theme.breakpoints.down('sm')]: {
      padding: space[6],
    },
  };
});

export const CardHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.palette.tokens.space[2],
  marginBottom: theme.palette.tokens.space[1],
}));

export const TenantLogo = styled('img')(({ theme }) => ({
  alignSelf: 'flex-start',
  height: '28px',
  width: 'auto',
  maxWidth: '160px',
  objectFit: 'contain',
  marginBottom: theme.palette.tokens.space[2],
}));

export const CardTitle = styled(Typography)<{ component?: string }>(
  ({ theme }) => ({
    ...theme.typography.h5,
    color: theme.palette.text.primary,
  })
);

export const CardSubtitle = styled(Typography)(({ theme }) => ({
  ...theme.typography.body2,
  color: theme.palette.text.secondary,
}));

export const Field = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
  '& label': {
    ...theme.typography.subtitle2,
    color: theme.palette.text.secondary,
  },
}));

export const Input = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    minHeight: '40px',
  },
  '& .MuiFormHelperText-root': {
    marginLeft: 0,
    marginRight: 0,
  },
  'input:-webkit-autofill': {
    WebkitBoxShadow: `0 0 0 1000px ${theme.palette.background.paper} inset`,
    WebkitTextFillColor: theme.palette.text.primary,
  },
}));

export const SubmitButton = styled(Button)(({ theme }) => ({
  width: '100%',
  minHeight: '44px',
  marginTop: theme.palette.tokens.space[2],
  fontSize: `${theme.palette.tokens.type.body.size}px`,
}));

export const GhostLink = styled(ButtonBase)(({ theme }) => ({
  alignSelf: 'center',
  ...theme.typography.body2,
  color: theme.palette.text.secondary,
  padding: '6px 8px',
  borderRadius: `${theme.palette.tokens.radius.control}px`,
  '&:hover': {
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.action.hover,
  },
}));

export const CardFooter = styled(Typography)(({ theme }) => ({
  ...theme.typography.caption,
  color: theme.palette.text.disabled,
  textAlign: 'center',
  marginTop: theme.palette.tokens.space[2],
}));

export const LoadingBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  padding: theme.palette.tokens.space[8],
}));
